import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { TransitionGroup, CSSTransition } from 'react-transition-group';

import { ChatIcon } from '@lets-talk/lt-chat-elements';
import { setDesiredWidgetDisplayState } from 'shared/actions/App';
import { selectWidgetBoxState } from 'shared/selectors/InitialData';
import { selectUnreadMessages } from 'shared/selectors/Conversation';

import LottieAnimation from '../../components/LottieAnimation/Loadable';

import {
  WIDGETBOX_ANIMATION_DECREASE_EFFECT,
  WIDGETBOX_ANIMATION_GROW_EFFECT,
  WIDGETBOX_ANIMATION_ENTER_DURATION,
  WIDGETBOX_ANIMATION_EXIT_DURATION,
} from '../../constants';

export const withWidgetBox = ({
  MinimizedComponent = null,
  MaximizedComponent = null,
  onMaximize = null,
}) => (WrappedComponent) => {
  class WidgetChrome extends Component {
    constructor(props) {
      super(props);

      this.maximize = this.maximize.bind(this);
      this.renderChatIcon = this.renderChatIcon.bind(this);
      this.renderLottieAnimation = this.renderLottieAnimation.bind(this);
    }

    maximize() {
      const { dispatch } = this.props;
      dispatch(setDesiredWidgetDisplayState('small'));
      if (onMaximize) dispatch(onMaximize());
    }

    renderLottieAnimation() {
      const { widgetBoxState } = this.props;
      const {
        frame, animationUrl,
      } = widgetBoxState.launcherSettings;
      const { icon } = frame;
      const { renderSettings } = icon;

      const options = {
        loop: true,
        autoplay: true,
        rendererSettings: renderSettings,
      };

      return (
        <div
          style={launcherStyles}
          onClick={this.maximize}
          role="button"
          tabIndex="0"
          onKeyUp={this.maximize}
        >
          <LottieAnimation
            animationUrl={animationUrl}
            options={options}
          />
        </div>
      );
    }

    renderChatIcon() {
      const { widgetBoxState, newMessages } = this.props;
      const {
        title, imageUrl, frame,
      } = widgetBoxState.launcherSettings;
      const { icon } = frame;
      const { width, height, borderRadius } = icon;

      return (
        <div style={launcherStyles}>
          {MinimizedComponent ? <MinimizedComponent {...this.props} />
            : (
              <ChatIcon
                newMessages={newMessages}
                onClick={this.maximize}
                text={title}
                width={width}
                height={height}
                chatIconRadius={borderRadius}
                imageUrl={imageUrl}
                margin="0px"
                disabled={false}
                animationStatus={false}
              />
            )}
        </div>
      );
    }

    renderLauncher() {
      const { widgetBoxState } = this.props;
      const {
        mode,
      } = widgetBoxState.launcherSettings;

      if (mode === 'animation') {
        return this.renderLottieAnimation();
      }
      return this.renderChatIcon();
    }

    renderBox() {
      return (
        <div style={containerStyles}>
          {MaximizedComponent ? <MaximizedComponent {...this.props} />
            : (
              <WrappedComponent
                {...this.props}
              />
            )}
        </div>
      );
    }

    render() {
      const { widgetBoxState } = this.props;
      const { mode } = widgetBoxState.launcherSettings;
      const { widgetState = 'minimized' } = widgetBoxState;

      return (
        <div style={containerStyles}>
          <TransitionGroup>
            { widgetState === 'minimized' && (
              <CSSTransition
                key="with-widget-box-minimized"
                timeout={{ enter: WIDGETBOX_ANIMATION_ENTER_DURATION, exit: WIDGETBOX_ANIMATION_EXIT_DURATION }}
                classNames={`${WIDGETBOX_ANIMATION_DECREASE_EFFECT}${mode}`}
              >
                {this.renderLauncher()}
              </CSSTransition>
            )}
          </TransitionGroup>
          <TransitionGroup>
            { widgetState === 'small' && (
              <CSSTransition
                key="with-widget-box-small"
                timeout={{ enter: WIDGETBOX_ANIMATION_ENTER_DURATION, exit: WIDGETBOX_ANIMATION_EXIT_DURATION }}
                classNames={`${WIDGETBOX_ANIMATION_GROW_EFFECT}${mode}`}
              >
                {this.renderBox()}
              </CSSTransition>
            )}
          </TransitionGroup>
        </div>
      );
    }
  }

  WidgetChrome.propTypes = {
    newMessages: PropTypes.number,
    widgetBoxState: PropTypes.shape({
      widgetState: PropTypes.oneOf(['small', 'minimized', 'hidden']),
      launcherSettings: PropTypes.shape({
        animationUrl: PropTypes.string,
        imageUrl: PropTypes.string,
        title: PropTypes.string,
        mode: PropTypes.oneOf(['chatIcon', 'default', 'animation']),
        frame: PropTypes.shape({
          icon: PropTypes.shape({
            width: PropTypes.number,
            height: PropTypes.number,
            renderSettings: PropTypes.object,
            borderRadius: PropTypes.string,
          }),
          width: PropTypes.number,
          height: PropTypes.number,
        }),
        icon: PropTypes.shape({
          width: PropTypes.number,
          height: PropTypes.number,
        }),
      }),
    }),
    dispatch: PropTypes.func,
  };

  WidgetChrome.defaultProps = {
    widgetBoxState: {
      widgetState: 'minimized',
    },
  };

  const mapDispatchToProps = (dispatch) => ({
    dispatch,
  });

  const mapStateToProps = createStructuredSelector({
    widgetBoxState: selectWidgetBoxState,
    newMessages: selectUnreadMessages,
  });

  const withConnect = connect(mapStateToProps, mapDispatchToProps);

  return withConnect(WidgetChrome);
};

withWidgetBox.propTypes = {
  WrappedComponent: PropTypes.element,
};

withWidgetBox.defaultProps = {
  minimizedComponent: null,
  smallComponent: null,
};

const containerStyles = {
  position: 'fixed',
  bottom: '0',
  right: '0',
  width: '100%',
  height: '100%',
};

const launcherStyles = {
  cursor: 'pointer',
  position: 'fixed',
  bottom: 0,
  right: 0,
  width: '100%',
};
